/**
 * POS custom fee types — admin-managed registry for the cashier's "custom fee"
 * charge, mirroring the `pos-payment-methods` pattern (config in
 * `tenant_config.features.pos_custom_fees.types[]`, no dedicated table, typed
 * admin editor — no raw JSON).
 *
 * The cashier used to TYPE the label. The load-bearing part of this change is
 * not the dropdown, it's that the cashier no longer sends a label at all: the
 * server resolves it from the registry code, so "Delvery" becomes
 * unrepresentable and the daily report can finally group by fee type.
 *
 * The admin sets the LABEL only. The amount stays per-sale: a delivery fee
 * varies by distance, and pinning it would make the feature unusable for the
 * case that motivated it.
 */

export type PosCustomFeeType = {
  /** Stable slug stored on `orders.custom_fees[].code`. */
  code: string;
  /** What the cashier picks and what prints on the tape. */
  label: string;
  enabled: boolean;
};

export type PosCustomFeesConfig = {
  types: PosCustomFeeType[];
};

/**
 * EMPTY by default, unlike `POS_PAYMENT_METHODS_DEFAULT`.
 *
 * A POS is unusable without a tender, so that registry had to ship with one.
 * Custom fees are the opposite: seeding a "Delivery" button for a tenant that
 * doesn't charge one puts a wrong control in front of every cashier. An empty
 * registry hides the fee block at the till entirely.
 */
export const POS_CUSTOM_FEES_DEFAULT: PosCustomFeesConfig = { types: [] };

/**
 * Resolve a fee type by code. Returns `null` when the code is absent or the type
 * is DISABLED — a disabled type must not silently still be chargeable, which is
 * the whole point of being able to retire one.
 */
export function resolvePosCustomFeeType(
  cfg: PosCustomFeesConfig | null | undefined,
  code: string,
): PosCustomFeeType | null {
  if (!cfg) return null;
  const types = Array.isArray(cfg.types) ? cfg.types : [];
  const found = types.find((t) => t.code === code);
  if (!found || !found.enabled) return null;
  return found;
}

/** Build a blank, enabled type for the admin editor's "add row". */
export function makePosCustomFeeType(code = '', label = ''): PosCustomFeeType {
  return { code, label, enabled: true };
}

/** One fee as stored on `orders.custom_fees` and rendered on a receipt. */
export type OrderCustomFee = {
  /** Registry code, or null for a fee placed before the registry existed. */
  code: string | null;
  /** Snapshotted at sale time — renaming the type never rewrites this. */
  label: string;
  amount: string;
};

/** The subset of an order these helpers need, so callers can pass any DTO. */
export type CustomFeeBearingOrder = {
  customFees?: OrderCustomFee[] | null;
  customFeeLabel?: string | null;
  customFeeAmount?: string | number | null;
};

/**
 * The fee lines to render for an order — THE single source for every renderer:
 * the cashier tape, the reprint, the admin order drawer.
 *
 * Orders placed before `orders.custom_fees` existed carry `[]` plus the legacy
 * label/amount pair, so this synthesises one line from those. Every renderer
 * must call this and only this, or the same historical order shows a fee in one
 * place and nothing in another.
 *
 * `'Other fee'` is the fallback label because that is what the admin order
 * drawer already displayed for a fee whose label was never recorded.
 */
export function orderCustomFeeLines(
  order: CustomFeeBearingOrder,
): Array<{ label: string; amount: string }> {
  const fees = order.customFees;
  if (Array.isArray(fees) && fees.length > 0) {
    return fees.map((f) => ({ label: f.label, amount: f.amount }));
  }
  const legacy = Number(order.customFeeAmount ?? 0);
  if (!Number.isFinite(legacy) || legacy <= 0) return [];
  return [
    { label: order.customFeeLabel?.trim() || 'Other fee', amount: String(order.customFeeAmount) },
  ];
}

/** Max length of the composed `orders.custom_fee_label` one-liner. */
const COMPOSED_LABEL_MAX = 120;

/**
 * The single-line label written to the legacy `orders.custom_fee_label` column.
 *
 * ONE fee composes to exactly what it composed to before this feature existed,
 * so a single-fee order is byte-identical to the old behaviour. Several join
 * with ` + `, truncated — the column feeds summaries and exports that assume a
 * short string, not a receipt.
 */
export function composeCustomFeeLabel(fees: Array<{ label: string }>): string | null {
  if (fees.length === 0) return null;
  const joined = fees.map((f) => f.label).join(' + ');
  return joined.length <= COMPOSED_LABEL_MAX
    ? joined
    : `${joined.slice(0, COMPOSED_LABEL_MAX - 1)}…`;
}
